'use client';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import {
  Activity,
  CircleCheck,
  Database,
  FileClock,
  Globe2,
  ShieldCheck,
  Users,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@gre4t/ui';
import { api } from '@/lib/api';
import { PageHeading } from '@/components/page-heading';

export function OverviewContent() {
  const t = useTranslations();
  const summary = useQuery({
    queryKey: ['overview'],
    queryFn: async () => {
      const [users, sessions, audit, health] = await Promise.all([
        api.users(1),
        api.sessions(),
        api.audit(1),
        api.health(),
      ]);
      return {
        users: users.meta.total,
        sessions: sessions.length,
        audit: audit.meta.total,
        health: health.status,
      };
    },
  });
  const stats = [
    { label: t('overview.users'), value: summary.data?.users, icon: Users },
    {
      label: t('overview.activeSessions'),
      value: summary.data?.sessions,
      icon: Activity,
    },
    {
      label: t('overview.auditEvents'),
      value: summary.data?.audit,
      icon: FileClock,
    },
    {
      label: t('overview.systemHealth'),
      value: summary.data?.health === 'ok' ? t('overview.healthy') : '—',
      icon: CircleCheck,
    },
  ];
  const capabilities = [
    [ShieldCheck, 'overview.auth'],
    [Database, 'overview.database'],
    [Globe2, 'overview.localization'],
    [FileClock, 'overview.audit'],
  ] as const;
  return (
    <>
      <PageHeading
        eyebrow={t('overview.eyebrow')}
        title={t('overview.title')}
        description={t('overview.description')}
      />
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map(({ label, value, icon: Icon }) => (
          <Card key={label} className="shadow-panel overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-muted-foreground text-xs font-medium">
                    {label}
                  </p>
                  {summary.isLoading ? (
                    <Skeleton className="mt-3 h-8 w-20" />
                  ) : (
                    <p className="mt-2 text-2xl font-bold tracking-tight">
                      {value}
                    </p>
                  )}
                </div>
                <div className="bg-primary/10 text-primary flex size-10 items-center justify-center rounded-xl">
                  <Icon className="size-[18px]" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </section>
      <Card className="shadow-panel mt-6">
        <CardHeader>
          <CardTitle>{t('overview.activityTitle')}</CardTitle>
          <CardDescription>{t('overview.activityDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {capabilities.map(([Icon, key]) => (
            <div
              key={key}
              className="bg-background/60 flex items-center gap-3 rounded-xl border p-4"
            >
              <div className="flex size-9 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                <Icon className="size-[18px]" />
              </div>
              <span className="text-sm font-medium">{t(key)}</span>
              <CircleCheck className="ms-auto size-4 text-emerald-500" />
            </div>
          ))}
        </CardContent>
      </Card>
    </>
  );
}
